/**
 * Keychain picker — one Pattern + many Letters (qty each) → bulk /cart/add.js
 */
class AycanKeychainPicker extends HTMLElement {
  connectedCallback() {
    if (this._booted) return;
    this._initAttempts = (this._initAttempts || 0) + 1;
    if (!this._canInit()) {
      if (this._initAttempts < 30) queueMicrotask(() => this.connectedCallback());
      return;
    }
    this._booted = true;

    const mapEl = this.querySelector('.aycan-keychain-variant-map');
    try {
      this.variants = JSON.parse(mapEl.textContent);
    } catch {
      this.variants = [];
    }
    this.selected = new Map();
    this.pattern = this._initialPattern();

    this.atcBtn = this.querySelector('.aycan-keychain-picker__atc');
    this.errorEl = this.querySelector('.aycan-design-picker__error');
    this.summaryEl = this.querySelector('.aycan-design-picker__summary');
    this.countEl = this.querySelector('.aycan-design-picker__count');
    this.spinner = this.atcBtn?.querySelector('.loading__spinner');
    this.atcLabel = this.atcBtn?.querySelector('.aycan-atc__label');
    this._defaultAtcLabel = this.atcLabel?.textContent?.trim() || '';
    this.color1Select = this.querySelector('[data-keychain-color="1"]');
    this.color2Select = this.querySelector('[data-keychain-color="2"]');

    this._syncPatternUI();
    this._refreshLetterAvailability();
    this._bindEvents();
    this._updateSummary();
  }

  disconnectedCallback() {
    this._booted = false;
    this._initAttempts = 0;
  }

  _canInit() {
    return Boolean(
      this.querySelector('.aycan-keychain-variant-map') &&
        this.querySelector('.aycan-keychain-picker__pattern-item') &&
        this.querySelector('.aycan-keychain-picker__letter-item')
    );
  }

  _initialPattern() {
    const selected = this.querySelector('.aycan-keychain-picker__pattern-item.aycan-swatch-item--selected');
    if (selected?.dataset.pattern) return selected.dataset.pattern;
    const firstItem = this.querySelector('.aycan-keychain-picker__pattern-item');
    if (firstItem?.dataset.pattern) return firstItem.dataset.pattern;
    const first = this.variants.find((v) => v.letter && v.pattern);
    return first?.pattern || 'Striped';
  }

  _variantFor(letter, pattern) {
    return this.variants.find((v) => v.letter === letter && v.pattern === pattern);
  }

  _bindEvents() {
    this.querySelectorAll('.aycan-keychain-picker__pattern-item .aycan-swatch__btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.aycan-keychain-picker__pattern-item');
        this._selectPattern(item.dataset.pattern);
      });
    });

    this.querySelectorAll('.aycan-keychain-picker__letter-item .aycan-swatch__btn').forEach((btn) => {
      btn.addEventListener('click', () => this._toggleLetter(btn.closest('.aycan-keychain-picker__letter-item')));
    });

    this.querySelectorAll('.aycan-qty__minus').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._adjustQty(btn.closest('.aycan-keychain-picker__letter-item'), -1);
      });
    });

    this.querySelectorAll('.aycan-qty__plus').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._adjustQty(btn.closest('.aycan-keychain-picker__letter-item'), 1);
      });
    });

    this.querySelectorAll('.aycan-qty__input').forEach((input) => {
      input.addEventListener('change', () => {
        const item = input.closest('.aycan-keychain-picker__letter-item');
        const letter = item.dataset.letter;
        const val = Math.max(1, Math.min(99, parseInt(input.value, 10) || 1));
        input.value = val;
        if (this.selected.has(letter)) this.selected.get(letter).qty = val;
        this._updateSummary();
      });
    });

    this.atcBtn?.addEventListener('click', () => {
      if (this.selected.size === 0) {
        this._scrollToPicker();
        window.ewaTrack?.('pdp_atc_incomplete', { picker_type: 'keychain', reason: 'no_letters' });
        return;
      }
      if (!this._colorsReady()) {
        this.color1Select?.focus();
        window.ewaTrack?.('pdp_atc_incomplete', { picker_type: 'keychain', reason: 'no_colors' });
        return;
      }
      this._addToCart();
    });
    this.color1Select?.addEventListener('change', () => this._updateSummary());
    this.color2Select?.addEventListener('change', () => this._updateSummary());
  }

  _colorsReady() {
    return Boolean(this.color1Select?.value && this.color2Select?.value);
  }

  _colorProperties() {
    if (!this._colorsReady()) return null;
    return {
      'Color 1': this.color1Select.value,
      'Color 2': this.color2Select.value,
    };
  }

  _selectPattern(pattern) {
    if (!pattern || pattern === this.pattern) return;
    this.pattern = pattern;
    this._syncPatternUI();

    for (const letter of [...this.selected.keys()]) {
      if (!this._variantFor(letter, this.pattern)) this._deselectLetter(letter);
    }
    this._refreshLetterAvailability();
    this._updateSummary();
  }

  _syncPatternUI() {
    this.querySelectorAll('.aycan-keychain-picker__pattern-item').forEach((item) => {
      const on = item.dataset.pattern === this.pattern;
      item.classList.toggle('aycan-swatch-item--selected', on);
      item.querySelector('.aycan-swatch__btn')?.setAttribute('aria-pressed', String(on));
    });
  }

  _refreshLetterAvailability() {
    if (!this.pattern) this.pattern = this._initialPattern();
    this.querySelectorAll('.aycan-keychain-picker__letter-item').forEach((item) => {
      const ok = Boolean(this._variantFor(item.dataset.letter, this.pattern));
      const btn = item.querySelector('.aycan-swatch__btn');
      if (!btn) return;
      btn.disabled = !ok;
      btn.setAttribute('aria-disabled', String(!ok));
      if (!ok && item.classList.contains('aycan-swatch-item--selected')) {
        this._deselectLetter(item.dataset.letter);
      }
    });
  }

  _toggleLetter(item) {
    const btn = item.querySelector('.aycan-swatch__btn');
    if (btn.disabled) return;

    const letter = item.dataset.letter;
    if (item.classList.contains('aycan-swatch-item--selected')) {
      this._deselectLetter(letter);
    } else {
      const variant = this._variantFor(letter, this.pattern);
      if (!variant) return;
      const qtyEl = item.querySelector('.aycan-swatch__qty');
      const qty = parseInt(item.querySelector('.aycan-qty__input')?.value, 10) || 1;
      item.classList.add('aycan-swatch-item--selected');
      btn.setAttribute('aria-pressed', 'true');
      if (qtyEl) {
        qtyEl.hidden = false;
        qtyEl.setAttribute('aria-hidden', 'false');
        qtyEl.querySelectorAll('.aycan-qty__input, .aycan-qty__minus, .aycan-qty__plus').forEach((el) => {
          el.tabIndex = 0;
        });
      }
      this.selected.set(letter, { variantId: variant.id, qty });
    }
    this._updateSummary();
  }

  _deselectLetter(letter) {
    const item = this.querySelector(`.aycan-keychain-picker__letter-item[data-letter="${CSS.escape(letter)}"]`);
    if (!item) return;
    item.classList.remove('aycan-swatch-item--selected');
    const btn = item.querySelector('.aycan-swatch__btn');
    btn?.setAttribute('aria-pressed', 'false');
    const qtyEl = item.querySelector('.aycan-swatch__qty');
    if (qtyEl) {
      qtyEl.hidden = true;
      qtyEl.setAttribute('aria-hidden', 'true');
      qtyEl.querySelectorAll('.aycan-qty__input, .aycan-qty__minus, .aycan-qty__plus').forEach((el) => {
        el.tabIndex = -1;
      });
    }
    this.selected.delete(letter);
  }

  _adjustQty(item, delta) {
    const input = item.querySelector('.aycan-qty__input');
    if (!input) return;
    const letter = item.dataset.letter;
    const newVal = Math.max(1, Math.min(99, (parseInt(input.value, 10) || 1) + delta));
    input.value = newVal;
    if (this.selected.has(letter)) this.selected.get(letter).qty = newVal;
    this._updateSummary();
  }

  _getAtcLabels() {
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    return {
      selectLabel: dock?.dataset.selectLabel || 'Select designs',
      defaultLabel: this._defaultAtcLabel || dock?.dataset.atcLabel || 'Add to cart',
    };
  }

  _scrollToPicker() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const first = this.querySelector('.aycan-keychain-picker__letter-item .aycan-swatch__btn:not([disabled])');
    first?.focus({ preventScroll: true });
  }

  _applyBuyboxAtc(hasItems) {
    if (!this.atcBtn) return;
    const { selectLabel, defaultLabel } = this._getAtcLabels();
    const colorsReady = this._colorsReady();
    if (!hasItems) {
      this.atcBtn.disabled = false;
      this.atcBtn.classList.add('is-select-mode');
      if (this.atcLabel) this.atcLabel.textContent = selectLabel;
      return;
    }
    this.atcBtn.classList.remove('is-select-mode');
    if (this.atcLabel) this.atcLabel.textContent = defaultLabel;
    this.atcBtn.disabled = !colorsReady;
  }

  _updateSummary() {
    const count = this.selected.size;
    const totalQty = [...this.selected.values()].reduce((s, { qty }) => s + qty, 0);
    const hasItems = count > 0;

    if (this.countEl) {
      this.countEl.textContent = this._t('selected', count, null, `${count} selected`);
      this.countEl.classList.toggle('is-empty', !hasItems);
      this.countEl.setAttribute('aria-hidden', hasItems ? 'false' : 'true');
    }

    if (this.summaryEl) {
      this.summaryEl.hidden = !hasItems;
      if (hasItems) {
        this.summaryEl.textContent =
          totalQty === count
            ? this._t('letters_each', count, null, `${count} letter${count > 1 ? 's' : ''} × 1 each`)
            : this._t('letters_total', count, { total: totalQty }, `${count} letters, ${totalQty} total`);
      }
    }

    this._applyBuyboxAtc(hasItems);

    this._emitDockUpdate(hasItems);
  }

  _computeSelectionTotalCents() {
    let total = 0;
    for (const [, { variantId, qty }] of this.selected) {
      const variant = this.variants.find((v) => v.id === variantId);
      if (variant?.price) total += variant.price * qty;
    }
    return total;
  }

  /* Metin ve para: assets/aycan-picker-i18n.js, veri snippets/aycan-picker-strings.liquid.
     Yardımcı çağrı anında aranır (defer sırası garanti değil); yoksa İngilizce yedeğe düşer.
     Eskiden `Shopify.formatMoney` arıyordu ama o hiçbir yerde tanımlı değil: sabit `$`
     basılıyor, EUR'lu alıcı dolar işaretli tutar görüyordu. */
  _i18n() {
    const i18n = window.AycanPickerI18n;
    if (i18n && this._strings === undefined) this._strings = i18n.read(this);
    return i18n || null;
  }

  _t(key, qty, vars, fallback) {
    const i18n = this._i18n();
    const text = i18n ? i18n.plural(this._strings, key, qty, vars) : null;
    return text == null ? fallback : text;
  }

  _formatMoney(cents) {
    const i18n = this._i18n();
    return i18n ? i18n.money(this._strings, cents) : `$${(cents / 100).toFixed(2)}`;
  }

  _getDockThumb() {
    const items = [...this.querySelectorAll('.aycan-keychain-picker__letter-item.aycan-swatch-item--selected')];
    const last = items[items.length - 1];
    const img = last?.querySelector('.aycan-swatch__btn--image img');
    if (img?.currentSrc || img?.src) return img.currentSrc || img.src;
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    const thumb = dock?.querySelector('.pdp-mobile-dock__thumb');
    return thumb?.tagName === 'IMG' ? thumb.src : '';
  }

  _emitDockUpdate(hasItems) {
    if (typeof window.pdpDockUpdate !== 'function') return;

    const count = this.selected.size;
    const ready = hasItems && this._colorsReady();
    const totalCents = ready ? this._computeSelectionTotalCents() : 0;
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    const selectLabel = dock?.dataset.selectLabel || 'Select designs';
    const defaultLabel = this._defaultAtcLabel || dock?.dataset.atcLabel || 'Add to cart';

    window.pdpDockUpdate(this.dataset.sectionId, {
      thumbSrc: this._getDockThumb(),
      selectionLine: ready
        ? `${this._t('selected', count, null, `${count} selected`)} · ${this._formatMoney(totalCents)}`
        : '',
      ctaLabel: !hasItems ? selectLabel : defaultLabel,
      ctaDisabled: hasItems && !this._colorsReady(),
      ctaMode: !hasItems ? 'scroll' : 'add',
    });
  }

  async _addToCart() {
    if (this.selected.size === 0 || !this._colorsReady()) return;

    const properties = this._colorProperties();
    const items = [...this.selected.values()].map(({ variantId, qty }) => ({
      id: variantId,
      quantity: qty,
      properties,
    }));

    this._setLoading(true);

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = ['cart-icon-bubble'];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sections = cartDrawer.getSectionsToRender().map((s) => s.id);
      }

      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.description || 'Could not add to cart.');

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'aycan-keychain-picker',
        productVariantId: items[0]?.id,
        cartData: data,
      });

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }

      this._resetSelection();
      window.ewaTrack?.('pdp_add_to_cart', { picker_type: 'keychain', atc_items: items.length });
    } catch (err) {
      window.ewaTrack?.('pdp_atc_error', { picker_type: 'keychain', atc_error: err.message });
      this.errorEl.textContent = err.message;
      this.errorEl.hidden = false;
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(isLoading) {
    if (this.atcBtn) this.atcBtn.disabled = isLoading;
    this.spinner?.classList.toggle('hidden', !isLoading);
    this.atcLabel?.classList.toggle('hidden', isLoading);
    window.pdpDockUpdate?.(this.dataset.sectionId, { loading: isLoading });
    if (!isLoading) this._updateSummary();
  }

  _resetSelection() {
    for (const letter of [...this.selected.keys()]) this._deselectLetter(letter);
    if (this.color1Select) this.color1Select.selectedIndex = 0;
    if (this.color2Select) this.color2Select.selectedIndex = 0;
    this._updateSummary();
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

customElements.define('aycan-keychain-picker', AycanKeychainPicker);

/* keychain-picker v2026-06-26 */
